import {
  Box,
  Paper,
  Typography,
  Chip,
  Skeleton,
  Divider,
  Avatar,
  IconButton,
  Button,
  Dialog,
  TextField,
} from "@mui/material";
import {
  Person,
  Phone,
  Email,
  LocationOn,
  Elevator,
  People,
  Layers,
  MeetingRoom,
  Settings,
  StraightenOutlined,
  ArrowBack,
  RequestQuote,
  Pending,
  CheckCircle,
  Notes,
  CalendarToday,
  PictureAsPdf,
  Cancel,
  Architecture,
  Edit,
  Close,
  Save,
} from "@mui/icons-material";
import { fetchLeadById, rejectLead } from "../../api/LeadService";
import {
  fetchNewQuotationFields,
  createQuotation,
  fetchQuotationById,
  updateQuotation,
  openQuotationPdf,
} from "../../api/QuotationService";
import NewQuotationDialog from "../../components/quotation/NewQuotationDialog";
import EditQuotationDialog from "../../components/quotation/EditQuotationDialog";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import theme from "../../theme/theme";
import { createCustomerFromLead } from "../../api/CustomerService";
import { createProjectFromLead } from "../../api/ProjectService";
import { CreateProjectFromLeadDialog } from "../../components/project/CreateProjectFromLeadDialog";

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="500">
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const getLeadStatus = (lead) => {
  if (lead.status === "pending") {
    if (lead.latest_quotation_request) {
      if (lead.latest_quotation_request.status === "requested") {
        return {
          label: "Quotation Requested",
          color: "warning",
          icon: <RequestQuote />,
        };
      } else if (lead.latest_quotation_request.status === "created") {
        return { label: "Quotation Created", color: "success" };
      }
    }
    return { label: "Pending", color: "warning", icon: <Pending /> };
  } else if (lead.status === "approved") {
    return { label: "Approved", color: "success", icon: <CheckCircle /> };
  } else if (lead.status === "rejected") {
    return { label: "Rejected", color: "error", icon: <Cancel /> };
  }
  return { label: lead.status, color: "default" };
};

const quotationStatusConfig = {
  requested: { label: "Requested", color: "warning" },
  created: { label: "Created", color: "success" },
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [fetchingLead, setFetchingLead] = useState(true);

  // New Quotation dialog state
  const [openNewQuotation, setOpenNewQuotation] = useState(false);
  const [loadingQuotationFields, setLoadingQuotationFields] = useState(false);
  const [savingQuotation, setSavingQuotation] = useState(false);
  const [newQuotation, setNewQuotation] = useState({});
  const [quotationErrors, setQuotationErrors] = useState({});
  const [selectedQrId, setSelectedQrId] = useState(null);

  // Edit Quotation dialog state
  const [openEditQuotation, setOpenEditQuotation] = useState(false);
  const [loadingEditQuotation, setLoadingEditQuotation] = useState(false);
  const [updatingQuotation, setUpdatingQuotation] = useState(false);
  const [editQuotation, setEditQuotation] = useState({});
  const [editQuotationErrors, setEditQuotationErrors] = useState({});
  const [editQuotationId, setEditQuotationId] = useState(null);

  // Convert to Customer state
  const [convertingToCustomer, setConvertingToCustomer] = useState(false);

  // Convert to Project state
  const [openCreateProject, setOpenCreateProject] = useState(false);
  const [convertingToProject, setConvertingToProject] = useState(false);
  const [projectErrors, setProjectErrors] = useState({});

  // Reject lead state
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectingLead, setRejectingLead] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectErrors, setRejectErrors] = useState({});

  const fetchLead = async () => {
    setFetchingLead(true);
    try {
      const response = await fetchLeadById(id);
      setLead(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch lead details");
    } finally {
      setFetchingLead(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [id]);

  const handleConvertToCustomer = async () => {
    setConvertingToCustomer(true);
    try {
      await createCustomerFromLead(id);
      toast.success("Lead successfully converted to Customer");
      fetchLead(); // Refresh the lead details
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error?.message || "Failed to convert lead to customer");
      }
    } finally {
      setConvertingToCustomer(false);
    }
  };

  const handleCreateProject = async (payload) => {
    setConvertingToProject(true);
    setProjectErrors({});
    try {
      await createProjectFromLead(id, payload);
      toast.success("Project seamlessly created from Lead!");
      setOpenCreateProject(false);
      fetchLead();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setProjectErrors(error.response.data.errors);
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to convert lead to project",
        );
      }
    } finally {
      setConvertingToProject(false);
    }
  };

  const handleRejectLead = async () => {
    setRejectingLead(true);
    setRejectErrors({});
    try {
      await rejectLead(id, rejectionReason);
      toast.success("Lead rejected successfully");
      setOpenRejectDialog(false);
      fetchLead();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setRejectErrors(error.response.data.errors);
      } else {
        toast.error(error?.response?.data?.message || "Failed to reject lead");
      }
    } finally {
      setRejectingLead(false);
    }
  };

  const handleOpenNewQuotation = async (quotationRequestId) => {
    setSelectedQrId(quotationRequestId);
    setQuotationErrors({});
    setOpenNewQuotation(true);
    setLoadingQuotationFields(true);
    try {
      const response = await fetchNewQuotationFields(id);
      setNewQuotation(response.data || {});
    } catch (error) {
      toast.error(error.message || "Failed to fetch quotation fields");
    } finally {
      setLoadingQuotationFields(false);
    }
  };

  const handleSaveQuotation = async () => {
    setSavingQuotation(true);
    setQuotationErrors({});
    try {
      await createQuotation({
        ...newQuotation,
        quotation_requests_id: selectedQrId,
      });
      toast.success("Quotation created successfully");
      setOpenNewQuotation(false);
      fetchLead();
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        setQuotationErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to create quotation");
      }
    } finally {
      setSavingQuotation(false);
    }
  };

  const handleOpenEditQuotation = async (quotationId) => {
    setEditQuotationId(quotationId);
    setEditQuotationErrors({});
    setOpenEditQuotation(true);
    setLoadingEditQuotation(true);
    try {
      const response = await fetchQuotationById(quotationId);
      const revision = response.data?.latest_revision || {};
      setEditQuotation({
        ...revision,
        generated_id: response.data?.generated_id || "",
      });
    } catch (error) {
      toast.error(error.message || "Failed to fetch quotation details");
    } finally {
      setLoadingEditQuotation(false);
    }
  };

  const handleUpdateQuotation = async () => {
    setUpdatingQuotation(true);
    setEditQuotationErrors({});
    try {
      await updateQuotation(editQuotationId, editQuotation);
      toast.success("Quotation updated successfully");
      setOpenEditQuotation(false);
      fetchLead();
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        setEditQuotationErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to update quotation");
      }
    } finally {
      setUpdatingQuotation(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const CardSkeleton = () => (
    <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius, mb: 2 }}>
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      {[...Array(4)].map((_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
        >
          <Skeleton variant="circular" width={24} height={24} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={180} height={20} />
          </Box>
        </Box>
      ))}
    </Paper>
  );

  const QuotationSkeleton = () => (
    <Box>
      {[...Array(2)].map((_, i) => (
        <Box
          key={i}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Skeleton variant="text" width={120} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>
          <Skeleton variant="text" width="100%" height={18} />
          <Skeleton variant="text" width={140} height={16} />
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <IconButton
            onClick={() => navigate("/leads")}
            sx={{
              backgroundColor: theme.palette.primary.main + "12",
              "&:hover": { backgroundColor: theme.palette.primary.main + "25" },
            }}
          >
            <ArrowBack sx={{ color: theme.palette.primary.main }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="700">
              {fetchingLead ? (
                <Skeleton width={200} />
              ) : (
                lead?.client_name || "Lead Detail"
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fetchingLead ? (
                <Skeleton width={120} />
              ) : (
                `Lead #${lead?.id || ""}`
              )}
            </Typography>
          </Box>
          {!fetchingLead &&
            lead &&
            (() => {
              const status = getLeadStatus(lead);
              return (
                <Chip
                  icon={status.icon}
                  label={status.label}
                  color={status.color || "default"}
                  variant="outlined"
                  sx={{ fontWeight: 600, px: 1 }}
                />
              );
            })()}

          {!fetchingLead && lead && !lead.customer_id && (
            <Button
              variant="contained"
              onClick={handleConvertToCustomer}
              disabled={convertingToCustomer}
              startIcon={<Person />}
              sx={{
                borderRadius: theme.button.borderRadius,
                backgroundColor: theme.button.editBackgroundColor,
              }}
            >
              {convertingToCustomer ? "Converting..." : "Create Customer"}
            </Button>
          )}

          {!fetchingLead && lead && lead.customer_id && !lead.project && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenCreateProject(true)}
              disabled={convertingToProject}
              startIcon={<Architecture />}
              sx={{
                borderRadius: theme.button.borderRadius,
                backgroundColor: theme.button.editBackgroundColor,
              }}
            >
              Create Project
            </Button>
          )}

          {!fetchingLead && lead && lead.status === "pending" && (
            <Button
              variant="contained"
              onClick={() => {
                setRejectionReason("");
                setRejectErrors({});
                setOpenRejectDialog(true);
              }}
              startIcon={<Cancel />}
              sx={{
                borderRadius: theme.button.borderRadius,
                backgroundColor: "#ef5350",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              Reject Lead
            </Button>
          )}

          {!fetchingLead && lead && lead.project && (
            <Chip
              icon={<Architecture />}
              label={`Project ${lead.project.project_id}`}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600, px: 1 }}
              onClick={() => navigate(`/projects/${lead.project.id}`)}
              clickable
            />
          )}

          {!fetchingLead && lead && lead.customer_id && (
            <Chip
              icon={<Person />}
              label="Customer Attached"
              color="success"
              variant="filled"
              sx={{ fontWeight: 600, px: 1 }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2.5 }}>
          {/* Left Column — 60% */}
          <Box sx={{ flex: "0 0 60%", minWidth: 0 }}>
            {/* Client Details Card */}
            {fetchingLead ? (
              <CardSkeleton />
            ) : (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: theme.paper.borderRadius,
                  backgroundColor: theme.paper.backgroundColor,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color={theme.palette.primary.main}
                  sx={{ mb: 2 }}
                >
                  Client Details
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <InfoRow
                    icon={<Person fontSize="small" />}
                    label="Name"
                    value={lead?.client_name}
                  />
                  <InfoRow
                    icon={<Phone fontSize="small" />}
                    label="Phone"
                    value={lead?.client_phone}
                  />
                </Box>
                <Divider />
                <InfoRow
                  icon={<Email fontSize="small" />}
                  label="Email"
                  value={lead?.client_email}
                />
                <Divider />
                <InfoRow
                  icon={<LocationOn fontSize="small" />}
                  label="Address"
                  value={lead?.client_address}
                />
                {lead?.latitude && lead?.longitude && (
                  <>
                    <Divider />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1,
                      }}
                    >
                      <LocationOn
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <a
                        href={`https://www.google.com/maps?q=${lead.latitude},${lead.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 13,
                          color: theme.palette.primary.main,
                          textDecoration: "none",
                        }}
                      >
                        View on Google Maps ↗
                      </a>
                    </Box>
                  </>
                )}
              </Paper>
            )}

            {/* Lift Requirements Card */}
            {fetchingLead ? (
              <CardSkeleton />
            ) : (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: theme.paper.borderRadius,
                  backgroundColor: theme.paper.backgroundColor,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color={theme.palette.primary.main}
                  sx={{ mb: 2 }}
                >
                  Lift Requirements
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <InfoRow
                    icon={<Elevator fontSize="small" />}
                    label="Lift Type"
                    value={
                      lead?.lift_type
                        ? lead.lift_type.charAt(0).toUpperCase() +
                          lead.lift_type.slice(1)
                        : "-"
                    }
                  />
                  <InfoRow
                    icon={<Elevator fontSize="small" />}
                    label="Cabin Type"
                    value={lead?.cabin_type || "-"}
                  />
                  <InfoRow
                    icon={<People fontSize="small" />}
                    label="Capacity (Persons)"
                    value={
                      lead?.capacity_persons
                        ? `${lead.capacity_persons} persons`
                        : "-"
                    }
                  />
                  <InfoRow
                    icon={<People fontSize="small" />}
                    label="Capacity (Kg)"
                    value={lead?.capacity_kg ? `${lead.capacity_kg} kg` : "-"}
                  />
                  <InfoRow
                    icon={<Layers fontSize="small" />}
                    label="No. of Floors"
                    value={lead?.no_of_floors}
                  />
                  <InfoRow
                    icon={<MeetingRoom fontSize="small" />}
                    label="Door Type"
                    value={
                      lead?.door_type
                        ? lead.door_type.charAt(0).toUpperCase() +
                          lead.door_type.slice(1) +
                          (lead.door_type === "manual" && lead.is_swing_door
                            ? " (Swing)"
                            : "")
                        : "-"
                    }
                  />
                  <InfoRow
                    icon={<Settings fontSize="small" />}
                    label="Configuration"
                    value={
                      lead?.lift_configuration
                        ? lead.lift_configuration.charAt(0).toUpperCase() +
                          lead.lift_configuration.slice(1)
                        : "-"
                    }
                  />
                  <InfoRow
                    icon={<StraightenOutlined fontSize="small" />}
                    label="Shaft Dimensions"
                    value={
                      lead?.shaft_length && lead?.shaft_width
                        ? `${lead.shaft_length} × ${lead.shaft_width} ft`
                        : "-"
                    }
                  />
                </Box>
                {lead?.additional_notes && (
                  <>
                    <Divider sx={{ mt: 1 }} />
                    <InfoRow
                      icon={<Notes fontSize="small" />}
                      label="Additional Notes"
                      value={lead.additional_notes}
                    />
                  </>
                )}
              </Paper>
            )}

            {/* Sales Person Card */}
            {fetchingLead ? (
              <CardSkeleton />
            ) : lead?.creator ? (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: theme.paper.borderRadius,
                  backgroundColor: theme.paper.backgroundColor,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color={theme.palette.primary.main}
                  sx={{ mb: 2 }}
                >
                  Sales Person
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.palette.primary.main + "20",
                      color: theme.palette.primary.main,
                      width: 48,
                      height: 48,
                      fontWeight: "bold",
                    }}
                  >
                    {lead.creator.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="600">
                      {lead.creator.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.creator.phone} · {lead.creator.email}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ) : null}
          </Box>

          {/* Right Column — 40% */}
          <Box sx={{ flex: "0 0 40%", minWidth: 0 }}>
            <Paper
              sx={{
                p: 3,
                borderRadius: theme.paper.borderRadius,
                backgroundColor: theme.paper.backgroundColor,
                position: "sticky",
                top: 16,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color={theme.palette.primary.main}
                >
                  Quotation Requests
                </Typography>
                {!fetchingLead && lead?.quotation_requests && (
                  <Chip
                    label={lead.quotation_requests.length}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: theme.palette.primary.main + "18",
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>

              {fetchingLead ? (
                <QuotationSkeleton />
              ) : lead?.quotation_requests?.length > 0 ? (
                lead.quotation_requests.slice().reverse().map((qr) => (
                  <Box
                    key={qr.id}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.primary.main + "06",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <RequestQuote
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                        <Typography variant="body2" fontWeight="600">
                          Request #{qr.id}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          quotationStatusConfig[qr.status]?.label || qr.status
                        }
                        color={
                          quotationStatusConfig[qr.status]?.color || "default"
                        }
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, height: 24, fontSize: 11 }}
                      />
                    </Box>
                    {qr.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {qr.notes}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <CalendarToday
                          sx={{ fontSize: 12, color: "text.disabled" }}
                        />
                        <Typography variant="caption" color="text.disabled">
                          {formatDate(qr.created_at)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {qr.status === "requested" ? (
                          <Chip
                            label="Create Quotation"
                            size="small"
                            onClick={() => handleOpenNewQuotation(qr.id)}
                            sx={{
                              fontWeight: 700,
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.main + "CC",
                              },
                            }}
                          />
                        ) : qr.status === "created" ? (
                          <>
                            <Chip
                              icon={<PictureAsPdf sx={{ fontSize: 16 }} />}
                              label="View PDF"
                              size="small"
                              onClick={async () => {
                                const revision = qr.quotation?.latest_revision;
                                if (revision?.id) {
                                  try {
                                    await openQuotationPdf(
                                      qr.quotation.id,
                                      revision.id,
                                    );
                                  } catch (e) {
                                    toast.error(
                                      e.message || "Failed to load PDF",
                                    );
                                  }
                                } else {
                                  toast.info(
                                    "No PDF available for this quotation",
                                  );
                                }
                              }}
                              sx={{
                                fontWeight: 700,
                                backgroundColor: "#e53935",
                                color: "#fff",
                                cursor: "pointer",
                                "& .MuiChip-icon": { color: "#fff" },
                                "&:hover": { backgroundColor: "#c62828" },
                              }}
                            />
                            <Chip
                              icon={<Edit sx={{ fontSize: 16 }} />}
                              label="Edit"
                              size="small"
                              onClick={() =>
                                handleOpenEditQuotation(qr.quotation?.id)
                              }
                              sx={{
                                fontWeight: 700,
                                backgroundColor: theme.palette.primary.main,
                                color: "#fff",
                                cursor: "pointer",
                                "& .MuiChip-icon": { color: "#fff" },
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.primary.main + "CC",
                                },
                              }}
                            />
                          </>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <RequestQuote
                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No quotation requests yet
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Attached Customer Card - Full width below */}
      {!fetchingLead && lead?.customer && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight="700" sx={{ mb: 2 }}>
            Attached Customer
          </Typography>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main + "20",
                    color: theme.palette.primary.main,
                    width: 48,
                    height: 48,
                  }}
                >
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    {lead.customer.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer #{lead.customer.id}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <InfoRow
                icon={<Email sx={{ fontSize: 20 }} />}
                label="Email"
                value={lead.customer.email || "N/A"}
              />
              <InfoRow
                icon={<Phone sx={{ fontSize: 20 }} />}
                label="Phone"
                value={lead.customer.phone || "N/A"}
              />
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20 }} />}
                label="Address"
                value={lead.customer.address || "N/A"}
              />
            </Box>
          </Paper>
        </Box>
      )}

      <NewQuotationDialog
        open={openNewQuotation}
        onClose={() => setOpenNewQuotation(false)}
        loading={loadingQuotationFields}
        saving={savingQuotation}
        quotation={newQuotation}
        setQuotation={setNewQuotation}
        onSave={handleSaveQuotation}
        errors={quotationErrors}
      />

      <EditQuotationDialog
        open={openEditQuotation}
        onClose={() => setOpenEditQuotation(false)}
        loading={loadingEditQuotation}
        saving={updatingQuotation}
        quotation={editQuotation}
        setQuotation={setEditQuotation}
        onSave={handleUpdateQuotation}
        errors={editQuotationErrors}
      />

      <CreateProjectFromLeadDialog
        open={openCreateProject}
        onClose={() => setOpenCreateProject(false)}
        saving={convertingToProject}
        onSave={handleCreateProject}
        errors={projectErrors}
      />

      {/* Reject Lead Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.dialog.borderRadius,
            minWidth: theme.dialog.minWidth,
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.dialog.headerBgColor,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={"bold"} color="error">
              Reject Lead
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={() => setOpenRejectDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this lead. This action cannot
            be undone.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={rejectingLead}
            error={!!rejectErrors.rejection_reason}
            helperText={rejectErrors.rejection_reason?.[0]}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: theme.dialog.headerBgColor,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejectingLead}
            sx={{
              backgroundColor: theme.button.backgroundColor,
              color: "#000",
              borderRadius: theme.button.borderRadius,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectLead}
            disabled={rejectingLead || !rejectionReason.trim()}
            sx={{
              backgroundColor: "#ef5350",
              borderRadius: theme.button.borderRadius,
              "&:hover": { backgroundColor: "#d32f2f" },
            }}
            startIcon={<Cancel />}
          >
            {rejectingLead ? "Rejecting..." : "Reject Lead"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default LeadDetailPage;
